import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { selectCurrentStreamingSteps } from 'modules/chatbot/slices/chatbotSlice';

import styles from './StatusSteps.module.less';

interface StatusStepsProps {
  onlySpinner?: boolean;
  defaultLoaders?: boolean;
  messageKey?: number | string; // Used to reset timers when a new message is submitted
  isLoading?: boolean; // Used to determine when to show defaultLoaders
}

const LOADING_MESSAGES = [
  'Reviewing your question...',
  'Researching available sources..',
  'Generating a response...',
];

const LOADING_TIMINGS = [10000, 20000]; // 10s, 20s

const StatusSteps: React.FC<StatusStepsProps> = ({
  defaultLoaders = false,
  messageKey,
  isLoading = false,
}) => {
  const steps = useSelector(selectCurrentStreamingSteps);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [loadingMessageIndex, setLoadingMessageIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const statusBubbleRef = useRef<HTMLDivElement>(null);
  const loadingTimersRef = useRef<NodeJS.Timeout[]>([]);

  // Effect for loading message transitions (defaultLoaders mode)
  // Resets and restarts timers whenever messageKey changes (new message submitted)
  useEffect(() => {
    if (!defaultLoaders) return;

    // Clear any existing timers
    loadingTimersRef.current.forEach(clearTimeout);
    loadingTimersRef.current = [];

    // Reset to first message
    setLoadingMessageIndex(0);
    setIsTransitioning(false);

    // Set up timers for transitions
    LOADING_TIMINGS.forEach((timing, index) => {
      const timer = setTimeout(() => {
        setIsTransitioning(true);
        setTimeout(() => {
          setLoadingMessageIndex(index + 1);
          setIsTransitioning(false);
        }, 300); // Fade duration
      }, timing);
      loadingTimersRef.current.push(timer);
    });

    return () => {
      loadingTimersRef.current.forEach(clearTimeout);
      loadingTimersRef.current = [];
    };
  }, [defaultLoaders, messageKey]);

  useEffect(() => {
    if (!steps || steps.length === 0) return;

    const interval = setInterval(() => {
      setCurrentStepIndex((prevIndex) => {
        const nextIndex = prevIndex + 1;
        return nextIndex >= steps.length ? steps.length - 1 : nextIndex;
      });
    }, 2000);

    return () => clearInterval(interval);
  }, [steps]);

  useEffect(() => {
    // Reset to first step when steps array changes
    setCurrentStepIndex(0);
  }, [steps]);

  // Auto-scroll to the status bubble when content changes
  useEffect(() => {
    if (statusBubbleRef.current && steps && steps.length > 0) {
      statusBubbleRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'end',
        inline: 'nearest',
      });
    }
  }, [currentStepIndex, steps]);

  // For defaultLoaders mode: only show when isLoading is true
  // For normal mode: only show when steps has items
  if (defaultLoaders) {
    if (!isLoading) {
      return <></>;
    }
  } else {
    if (!steps || steps.length === 0) {
      return <></>;
    }
  }

  // Render status bubble - same structure for both defaultLoaders and normal mode
  // Only the text content and transition behavior differ
  return (
    <div
      className={styles.statusBubble}
      ref={statusBubbleRef}
      data-testid="chatbot-status-bubble"
    >
      <div className={styles.spinner} data-testid="chatbot-status-spinner">
        <div
          className={styles.spinnerIcon}
          data-testid="chatbot-status-spinner-icon"
        ></div>
      </div>
      <div
        className={styles.statusTextContainer}
        data-testid="chatbot-status-text-container"
      >
        <div
          className={`${styles.statusText} ${
            defaultLoaders && isTransitioning ? styles.fadeOut : styles.fadeIn
          }`}
          key={defaultLoaders ? loadingMessageIndex : currentStepIndex}
          data-testid="chatbot-status-text"
        >
          {defaultLoaders
            ? LOADING_MESSAGES[loadingMessageIndex]
            : steps?.[currentStepIndex]}
        </div>
      </div>
    </div>
  );
};

export default StatusSteps;
